/**
 * Sign-in, with the privacy notice RA 10173 asks for (§8).
 *
 * The artwork is a mockup: the form in it was painted on. Those controls have
 * been cleared from the image, and the real form is placed in the space they
 * left. To keep it there at any window size, the picture and the form share one
 * stage sized to the image's own proportions — so they scale together instead
 * of drifting apart. Below `lg` the stage is dropped entirely: on a phone the
 * panel would be thumbnail-sized, so the picture becomes a backdrop and the
 * form a normal card.
 */
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, ShieldCheck } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { ApiError } from '../lib/api';
import { Alert, Button, Field, inputClass } from '../components/ui';

/** Where the cleared panel sits in the picture, as a share of it. */
const PANEL = { left: '37.6%', width: '27.5%', top: '46.5%' };

export function LoginPage(): JSX.Element {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const onSubmit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const user = await signIn(email.trim(), password);
      navigate(user.mustResetPassword ? '/change-password' : '/', { replace: true });
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not sign in. Try again.');
    } finally {
      setBusy(false);
    }
  };

  /** The fields themselves, shared by the two layouts. */
  const fields = (onDark: boolean): JSX.Element => {
    const box = onDark
      ? 'w-full rounded-[0.6em] border border-white/15 bg-white/5 px-[0.9em] py-[0.62em] text-[1em] text-white placeholder:text-white/40 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/40'
      : inputClass;

    return (
      <>
        {error && (
          <div className={onDark ? 'text-[0.85em]' : undefined}>
            <Alert tone="critical">{error}</Alert>
          </div>
        )}

        {onDark ? (
          <label className="block">
            <span className="sr-only">Email address</span>
            <input
              className={box}
              type="email"
              autoComplete="username"
              placeholder="Email address"
              required
              autoFocus
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </label>
        ) : (
          <Field label="Email address" required>
            <input
              className={box}
              type="email"
              autoComplete="username"
              required
              autoFocus
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </Field>
        )}

        {onDark ? (
          <label className="relative block">
            <span className="sr-only">Password</span>
            <input
              className={`${box} pr-[2.6em]`}
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="Password"
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
            <button
              type="button"
              className="absolute right-[0.5em] top-1/2 grid h-[1.6em] w-[1.6em] -translate-y-1/2 place-items-center rounded-md text-white/60 hover:text-white"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              aria-pressed={showPassword}
              onClick={() => setShowPassword((value) => !value)}
            >
              {showPassword ? (
                <EyeOff aria-hidden="true" className="h-[1em] w-[1em]" />
              ) : (
                <Eye aria-hidden="true" className="h-[1em] w-[1em]" />
              )}
            </button>
          </label>
        ) : (
          <Field label="Password" required>
            <div className="relative">
              <input
                className={`${box} pr-11`}
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                required
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
              <button
                type="button"
                className="absolute right-1 top-1/2 grid h-9 w-9 -translate-y-1/2 place-items-center rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                aria-pressed={showPassword}
                onClick={() => setShowPassword((value) => !value)}
              >
                {showPassword ? (
                  <EyeOff aria-hidden="true" className="h-4 w-4" />
                ) : (
                  <Eye aria-hidden="true" className="h-4 w-4" />
                )}
              </button>
            </div>
          </Field>
        )}

        {onDark ? (
          <button
            type="submit"
            disabled={busy}
            className="w-full rounded-[0.6em] bg-brand px-[1em] py-[0.66em] text-[1em] font-semibold text-white shadow-lg shadow-brand/30 transition hover:bg-brand-700 disabled:opacity-60"
          >
            {busy ? 'Signing in…' : 'Sign In'}
          </button>
        ) : (
          <Button type="submit" busy={busy} className="w-full">
            {busy ? 'Signing in…' : 'Sign in'}
          </Button>
        )}
      </>
    );
  };

  const privacy = (
    <span>
      This is a private system for ITCORE Solutions Inc. staff. Sign-ins, changes and downloads
      are recorded. Customer and contact details held here are personal data under RA 10173 and
      may only be used for legitimate business purposes.
    </span>
  );

  return (
    <>
      {/* ---------- large screens: the artwork, with the form set into it ---------- */}
      <div className="hidden min-h-screen place-items-center bg-[#050b18] lg:grid">
        <div
          className="relative w-full"
          style={{
            // The picture's own proportions, so the form's percentage
            // placement lands in the same spot whatever the window does.
            aspectRatio: '1600 / 900',
            maxWidth: 'min(100vw, calc(100vh * 1600 / 900))',
            containerType: 'size',
          }}
        >
          <img
            src="/brand/login.jpg"
            alt=""
            className="absolute inset-0 h-full w-full object-contain"
            fetchPriority="high"
          />

          <form
            onSubmit={(event) => void onSubmit(event)}
            noValidate
            className="absolute space-y-[0.7em]"
            style={{
              left: PANEL.left,
              width: PANEL.width,
              top: PANEL.top,
              // Everything inside is sized in ems off this, so the whole form
              // grows and shrinks with the picture rather than the viewport.
              fontSize: 'clamp(11px, 1.15cqw, 19px)',
            }}
          >
            {fields(true)}
          </form>

          <p
            className="absolute inset-x-0 bottom-[1.5%] mx-auto max-w-[62%] text-center text-white/45"
            style={{ fontSize: 'clamp(9px, 0.72cqw, 13px)' }}
          >
            © {new Date().getFullYear()} ITCORE Solutions Inc. · Sign-ins and changes are
            recorded. Details held here are personal data under RA 10173.
          </p>
        </div>
      </div>

      {/* ---------- phones and tablets: the picture behind a plain card ---------- */}
      <div className="relative grid min-h-screen place-items-center overflow-hidden px-4 py-12 lg:hidden">
        <img
          src="/brand/login-sm.jpg"
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-[#050b18]/80 backdrop-blur-[2px]" />

        <div className="relative w-full max-w-sm rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur-md">
          <p className="text-xl font-bold tracking-tight text-white">ITCORE SALES FORCE</p>
          <p className="mt-1 text-xs uppercase tracking-[0.18em] text-white/50">
            Leads · Sales · Projects · Finance
          </p>

          <form
            className="mt-6 space-y-3 text-[15px]"
            onSubmit={(event) => void onSubmit(event)}
            noValidate
          >
            {fields(true)}
          </form>

          <p className="mt-6 flex gap-2 text-[11px] leading-relaxed text-white/50">
            <ShieldCheck aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
            {privacy}
          </p>
        </div>
      </div>
    </>
  );
}
